import { collection, deleteDoc, doc, getDocs, onSnapshot, setDoc, type DocumentReference } from 'firebase/firestore';
import { db } from './firebase';
import {
  DEFAULT_COMPLIANCE_CATEGORIES,
  compliancePalette,
  MANDATORY_COMPLIANCE_CATEGORY_IDS,
  type ComplianceCategory,
} from './complianceCategoryTypes';

const normalizeTeamId = (teamId: unknown): string | undefined => {
  if (!teamId) return undefined;
  if (typeof teamId === 'string') return teamId.trim();
  if (typeof (teamId as DocumentReference).id === 'string') return (teamId as DocumentReference).id;
  return undefined;
};

const normalizeTags = (tags?: unknown[]): string[] => {
  if (!Array.isArray(tags)) return [];

  const seen = new Set<string>();
  const normalized: string[] = [];

  tags.forEach((tag) => {
    const value = typeof tag === 'string' ? tag.trim() : '';
    if (!value || seen.has(value)) return;
    seen.add(value);
    normalized.push(value);
  });

  return normalized;
};

const resolveDefaultCategory = (category?: { id?: unknown; title?: unknown }) =>
  DEFAULT_COMPLIANCE_CATEGORIES.find(
    (defaultCategory) =>
      (category?.id && defaultCategory.id === category.id) ||
      (category?.title && defaultCategory.title === category.title),
  );

const safeCategoryText = (value: unknown, fallback = ''): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
  if (typeof value === 'object') {
    const refLike = value as { id?: unknown; path?: unknown };
    if (typeof refLike.id === 'string' && refLike.id.trim()) return refLike.id.trim();
    if (typeof refLike.path === 'string' && refLike.path.trim()) {
      const last = refLike.path.split('/').filter(Boolean).pop();
      if (last) return last.trim();
    }
  }
  try {
    return String(value).trim();
  } catch {
    return fallback;
  }
};

const normalizeComplianceCategory = (category: Partial<ComplianceCategory>): ComplianceCategory => {
  const fallback = resolveDefaultCategory(category);

  const id = safeCategoryText(category.id ?? category.title ?? fallback?.id ?? '', '');
  const title = safeCategoryText(category.title ?? fallback?.title ?? category.id ?? '', '');

  const tags = normalizeTags(category.tags as unknown[]);

  const merged: ComplianceCategory = {
    ...(fallback ?? {}),
    ...(category as ComplianceCategory),
    id,
    title,
    dataMode: (category as ComplianceCategory).dataMode ?? fallback?.dataMode,
    teamId: normalizeTeamId((category as ComplianceCategory).teamId) ?? fallback?.teamId,
    tags,
    helpText:
      typeof (category as ComplianceCategory).helpText === 'string'
        ? (category as ComplianceCategory).helpText?.trim()
        : fallback?.helpText,
    severityDefault:
      typeof (category as ComplianceCategory).severityDefault === 'string'
        ? (category as ComplianceCategory).severityDefault?.trim()
        : fallback?.severityDefault,
    periodicity:
      typeof (category as ComplianceCategory).periodicity === 'string'
        ? (category as ComplianceCategory).periodicity
        : fallback?.periodicity,
    active: (category as ComplianceCategory).active ?? fallback?.active ?? true,
  };

  // Firestore must not override canonical nursing chart count / period label (stale 10 / ครั้งที่).
  if (id === 'nurse_notes' || title === 'บันทึกทางการพยาบาล') {
    merged.charts = fallback?.charts ?? 8;
    merged.periodLabel = fallback?.periodLabel ?? 'Chart';
  }

  // Same needle-default leak for med_error (charts: 10, ครั้งที่). Required rounds stay 4.
  if (id === 'med_error' || title === 'นิเทศระบบยา') {
    merged.charts = fallback?.charts ?? 4;
    merged.periodLabel = fallback?.periodLabel ?? 'สัปดาห์';
  }

  // Needle and sharp is a fixed 10-column paper table. Firestore must not hide it or change the column count.
  if (id === 'needleandsharp' || title === 'เข็มตำ/ของมีคมบาด') {
    merged.charts = fallback?.charts ?? 10;
    merged.periodLabel = fallback?.periodLabel ?? 'ครั้งที่';
    merged.active = true;
  }

  // SIREN bundle is a fixed 5-HN paper table. Firestore must not restore charts: 4.
  if (id === 'siren_bundle' || title === 'SIREN bundle') {
    merged.charts = fallback?.charts ?? 5;
    merged.periodLabel = fallback?.periodLabel ?? 'HN';
    merged.active = true;
  }

  return merged;
};

const stripUndefined = <T extends Record<string, unknown>>(payload: T): T =>
  Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined)) as T;

const normalizeComplianceCategoryPayload = (category: Partial<ComplianceCategory>): ComplianceCategory => {
  const normalized = normalizeComplianceCategory(category);

  return stripUndefined({
    ...normalized,
    charts: normalized.charts ?? undefined,
    departmentIds: normalized.departmentIds ?? undefined,
    userIds: normalized.userIds ?? undefined,
    tags: normalized.tags ?? undefined,
  }) as ComplianceCategory;
};

const ETHIC_SURVEYS_CATEGORY = DEFAULT_COMPLIANCE_CATEGORIES.find((c) => c.id === 'ethicSurveys');

export const resolveComplianceCategories = (categories?: Partial<ComplianceCategory>[]) => {
  if (!categories?.length) return DEFAULT_COMPLIANCE_CATEGORIES;

  const resolved = categories.map((category) => normalizeComplianceCategory(category));
  const hasEthicSurveys = resolved.some((c) => c.id === 'ethicSurveys');
  if (!hasEthicSurveys && ETHIC_SURVEYS_CATEGORY)
    resolved.push({ ...ETHIC_SURVEYS_CATEGORY, active: ETHIC_SURVEYS_CATEGORY.active ?? true });
  return resolved;
};

// Use DEFAULT_COMPLIANCE_CATEGORIES directly to avoid TDZ issues with minification
export const complianceCategories = DEFAULT_COMPLIANCE_CATEGORIES;

export const getCategoryById = (categoryId: string): ComplianceCategory | undefined =>
  DEFAULT_COMPLIANCE_CATEGORIES.find((category) => category.id === categoryId || category.title === categoryId);

const COMPLIANCE_CATEGORY_COLLECTION = 'complianceCategories';

export const getComplianceCategories = async (): Promise<ComplianceCategory[]> => {
  const categoriesCollection = collection(db, COMPLIANCE_CATEGORY_COLLECTION);

  const snapshot = await getDocs(categoriesCollection);

  if (snapshot.empty) return DEFAULT_COMPLIANCE_CATEGORIES;

  const categories = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as Record<string, unknown>) }));

  return resolveComplianceCategories(categories);
};

export const subscribeComplianceCategories = (
  onNext: (categories: ComplianceCategory[]) => void,
  onError?: (error: Error) => void,
) => {
  const categoriesCollection = collection(db, COMPLIANCE_CATEGORY_COLLECTION);

  return onSnapshot(
    categoriesCollection,
    (snapshot) => {
      if (snapshot.empty) {
        onNext(DEFAULT_COMPLIANCE_CATEGORIES);
        return;
      }

      const categories = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as Record<string, unknown>) }));
      onNext(resolveComplianceCategories(categories));
    },
    (error) => {
      onError?.(error as Error);
    },
  );
};

export const addComplianceCategory = async (category: ComplianceCategory) => {
  const categoryRef = doc(db, COMPLIANCE_CATEGORY_COLLECTION, category.id);

  const { id: _id, ...payload } = normalizeComplianceCategoryPayload(category);

  try {
    await setDoc(categoryRef, payload);
  } catch (err) {
    console.error('🔥 Firestore write failed:', err);
    throw err;
  }
};

export const updateComplianceCategory = async (category: ComplianceCategory) => {
  const categoryRef = doc(db, COMPLIANCE_CATEGORY_COLLECTION, category.id);

  const { id: _id, ...payload } = normalizeComplianceCategoryPayload(category);

  await setDoc(categoryRef, payload, { merge: true });
};

export const deleteComplianceCategory = async (categoryId: string) => {
  const categoryRef = doc(db, COMPLIANCE_CATEGORY_COLLECTION, categoryId);
  await deleteDoc(categoryRef);
};

export { DEFAULT_COMPLIANCE_CATEGORIES, MANDATORY_COMPLIANCE_CATEGORY_IDS, compliancePalette };
export type { ComplianceCategory };
